<template>
  <div
    ref="container"
    :class="className"
    class="datepicker">
    <div class="btn-group summary-group">
      <button
        class="btn white prev"
        @click="previousRange()"/>
      <div class="summary">
        <button
          :class="{'popup-open': showPopup, 'popup-close': !showPopup}"
          class="btn white"
          @click="popup()">{{ rangeText }}</button>
      </div>
      <button
        class="btn white next"
        @click="nextRange()"/>
    </div>
    <div
      v-if="showPopup"
      class="datepicker-popup">
      <!-- enhance filter, temporary checking -->
      <div v-show="path && fuelEnabled">
        <div class="filter-mode btn-group">
          <button
            :class="{active: true}"
            type="button"
            class="btn btn-sm white"
            @click="openDatePicker()">
            Date
          </button>
        </div>
      </div>
      <!-- enhance filter -->
      <div
        v-show="showDatePicker"
        class="datepicker-inner">
        <div class="datepicker-body">
          <div
            class="datepicker-dateInput">
            <div class="datepicker-ctrl">
              <span
                v-show="canGoPrevious"
                class="datepicker-preBtn glyphicon glyphicon-chevron-left"
                aria-hidden="true"
                @click="previous()"/>
              <span
                v-show="canGoNext"
                class="datepicker-nextBtn glyphicon glyphicon-chevron-right"
                aria-hidden="true"
                @click="next()"/>
              <p>{{ currentDate.format("YYYY") }}</p>
            </div>
            <div class="datepicker-dateRange">
              <span
                v-for="(d, index) in dateRange"
                :key="index"
                :class="d.sclass"
                @click="selectDate(d.date)">{{ d.text }}</span>
            </div>
          </div>
          <div
            class="shortcut-selector">
            <button
              class="btn btn-sm white"
              @click="applyDate()">Apply</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import leftPad from 'left-pad';
import m from 'moment';
import range from 'moment-range';
import twix from 'twix';
import Feature from '@js/plugins/feature';

export default {
    props: {
        format: {
            type: String,
            default: 'YYYY-MM',
        },
        min: {
            type: Object,
            default: () => m('2014-01'),
        },
        max: {
            type: Object,
            default: () => m().endOf('day'),
        },
        start: {
            type: Object,
            default: null,
        },
        end: {
            type: Object,
            default: null,
        },
        useVuex: {
            type: Boolean,
            default: true,
        },
        className: {
            type: String,
            default: '',
        },
        autoInitDate: {
            type: Boolean,
            default: true,
        },
        allowEmpty: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            currentDate: m(),
            startMonth: m().startOf('month'),
            endMonth: m().endOf('month'),
            dateRange: [],
            hasStart: true,
            hasEnd: true,
            showPopup: false,
            showShift: false,
            showDatePicker: true,
            path:
                window.location.pathname === '/franchise/report/snapshot' ||
                window.location.pathname === '/franchise/report/transaction',
        };
    },

    computed: {
        canGoPrevious() {
            let newDate = m(this.currentDate).subtract(1, 'year');
            return newDate.isSameOrAfter(this.getMinimumDate().startOf('year'));
        },

        canGoNext() {
            let newDate = m(this.currentDate).add(1, 'year');
            return newDate.isSameOrBefore(this.getMaximumDate().endOf('year'));
        },

        startDateText() {
            return this.getStartDate().format('YYYY-MM');
        },

        endDateText() {
            return this.getEndDate().format('YYYY-MM');
        },

        rangeText() {
            if (this.noDateDefine()) {
                return 'Filter Date';
            } else {
                return m(this.getStartDate())
                    .twix(this.getEndDate(), { allDay: true })
                    .format({
                        implicitYear: false,
                        implicitMonth: false,
                        implicitDay: true,
                    });
            }
        },

        activeOutlet() {
            return this.$store.getters.activeOutlet;
        },
    },

    watch: {
        currentDate() {
            this.generateDateRange();
        },

        startMonth() {
            this.generateDateRange();
            this.handleMonthChange();
        },

        endMonth() {
            this.generateDateRange();
        },

        activeOutlet() {
            this.openDatePicker();

            this.$store.commit('setMonthReportRange', {
                start: this.startMonth,
                end: this.endMonth,
            });
        },
    },

    mounted() {
        document.addEventListener('click', this.handleClickOutside, true);
        this.setStartAndEndDate();
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside, true);
    },

    methods: {
        applyDate() {
            this.broadcastChange();
            this.closePopup();
        },

        popup() {
            if (this.showPopup) {
                this.closePopup();
            } else {
                this.openPopup();
            }
        },

        openPopup() {
            this.showPopup = true;
            this.generateDateRange();
            this.openDatePicker();
        },

        closePopup(s) {
            this.showPopup = false;
        },

        openDatePicker() {
            this.showDatePicker = true;
        },

        handleClickOutside(e) {
            const el = this.$refs.container;

            if (!el.contains(e.target)) {
                if (this.showPopup) {
                    this.closePopup();
                }
            }
        },

        generateDateRange() {
            this.dateRange = [];

            let that = this;

            let start = m(this.currentDate)
                .startOf('year')
                .startOf('month');

            let end = m(this.currentDate)
                .endOf('year')
                .endOf('month');

            if (
                !this.extraPreviousYearEnabled() &&
                this.extraNextYearEnabled()
            ) {
                end.subtract(1, 'year');
            }

            let range = m.range(start, end);

            range.by('month', function(moment) {
                that.dateRange.push({
                    date: moment.startOf('month'),
                    text: moment.format('MMM'),
                    sclass: that.getDateClass(moment),
                });
            });
        },

        clampDate(date) {
            if (date == null) {
                return;
            }

            if (date.isAfter(this.getMaximumDate())) {
                return this.getMaximumDate();
            }

            if (date.isBefore(this.getMinimumDate())) {
                return this.getMinimumDate();
            }

            return date;
        },

        selectDate(date) {
            const now = m();
            if (date.isSame(now, 'month')) {
                this.selectRange(
                    date.clone().startOf('month'),
                    now.clone().endOf('day'),
                );
            } else {
                this.selectRange(
                    date.clone().startOf('month'),
                    date.clone().endOf('month'),
                );
            }
            this.hasEnd = false;
            this.broadcastChange();
        },

        setStartDate(date, time = true) {
            var finalDate = this.parseDate(date, null);

            if (finalDate != null && time) {
                finalDate = finalDate.startOf('month');
            }

            this.startMonth = this.clampDate(finalDate);
        },

        setEndDate(date, time = true) {
            var finalDate = this.parseDate(date, null);

            if (finalDate != null && time) {
                finalDate = finalDate.endOf('month');
            }

            if (finalDate != null && finalDate.isBefore(this.getStartDate())) {
                finalDate = m(this.getStartDate());
            }

            this.endMonth = this.clampDate(finalDate);
        },

        parseDate(date, def = m()) {
            if (m.isMoment(date)) {
                return m(date);
            }

            let parsed = m(date, this.format);

            if (parsed.isValid()) {
                return parsed;
            }

            return def;
        },

        navigateToDate(date) {
            if (!date) return;

            let month = date.month();
            let year = date.year();

            this.currentDate = m(this.currentDate)
                .month(month)
                .year(year);
        },

        selectRange(start, end, time = true) {
            this.setStartDate(start, time);
            this.setEndDate(end, time);
            this.navigateToDate(this.clampDate(start));
        },

        clearDate() {
            this.selectRange(null, null);
            this.closePopup();
        },

        selectThisMonth() {
            const now = m();
            this.selectRange(
                now.clone().startOf('month'),
                now.clone().endOf('day'),
            );
            this.closePopup();
        },

        handleMonthChange() {
            const now = m();
            if (this.startMonth && this.startMonth.isSame(now, 'month')) {
                this.endMonth = now.clone().endOf('day');
            }
            this.broadcastChange();
        },

        selectLastMonth() {
            var start = m()
                .subtract(1, 'month')
                .startOf('month')
                .startOf('year');
            var end = m()
                .subtract(1, 'month')
                .endOf('month')
                .endOf('year');
            this.selectRange(start, end);
            this.closePopup();
        },

        selectYear(date) {
            var start = m(this.currentDate)
                .startOf('month')
                .startOf('year');
            var end = m(this.currentDate)
                .endOf('month')
                .endOf('year');
            this.selectRange(start, end);
            this.closePopup();
        },

        setStartAndEndDate() {
            if (this.start == null && this.end == null) {
                if (this.autoInitDate) {
                    const now = m();
                    this.startMonth = now.clone().startOf('month');
                    this.endMonth = now.clone().endOf('day');
                } else {
                    this.clearDate();
                }
                return;
            }
            if (this.start != null) {
                this.startMonth = m(this.start, this.format);
            }
            if (this.end != null) {
                this.endMonth = m(this.end, this.format);
            }
        },

        extraPreviousYearEnabled() {
            return m(this.currentDate)
                .startOf('month')
                .endOf('year')
                .subtract(4, 'month')
                .isSame(this.currentDate, 'year');
        },

        extraNextYearEnabled() {
            return m(this.currentDate)
                .endOf('month')
                .startOf('year')
                .add(4, 'month')
                .isSame(this.currentDate, 'year');
        },

        getDateClass(date) {
            var clazz = [];

            if (
                date.isBetween(
                    this.getStartDate(),
                    this.getEndDate(),
                    'month',
                    '[]',
                )
            ) {
                clazz.push('datepicker-day-selected');
            }

            if (date.isSame(this.getStartDate(), 'month')) {
                clazz.push('datepicker-day-start');
            }

            if (date.isSame(this.getEndDate(), 'month')) {
                clazz.push('datepicker-day-end');
            }

            if (
                this.getMinimumDate().isAfter(date) ||
                this.getMaximumDate().isBefore(date)
            ) {
                clazz.push('datepicker-day-disabled');
            }

            return clazz.join(' ');
        },

        getStartDate() {
            if (this.startMonth == null) {
                return m().startOf('month');
            }

            return this.startMonth;
        },

        getEndDate() {
            if (this.endMonth == null) {
                if (this.startMonth != null) {
                    return this.getStartDate().endOf('month');
                } else {
                    return m().endOf('month');
                }
            }

            return this.endMonth;
        },

        getMinimumDate() {
            if (this.min != null) {
                return m(this.min, this.format);
            }

            return m('2014-01-01');
        },

        getMaximumDate() {
            if (this.max != null) {
                return m(this.max, this.format);
            }

            return m().endOf('month');
        },

        rangeIsExactlyOneYear() {
            let lastYear = m(this.getStartDate())
                .endOf('year')
                .date();
            let isFirstMonth = m(this.getStartDate()).date() == 1;
            let isLastMonth = m(this.getEndDate()).date() == lastYear;

            return isFirstMonth && isLastMonth;
        },

        previous() {
            if (this.canGoPrevious) {
                this.currentDate = m(this.currentDate).subtract(1, 'year');
            }
        },

        next() {
            if (this.canGoNext) {
                this.currentDate = m(this.currentDate).add(1, 'year');
            }
        },

        previousRange() {
            let start;
            let end;

            if (this.rangeIsExactlyOneYear()) {
                start = this.getStartDate()
                    .subtract(1, 'month')
                    .startOf('month');

                end = this.getEndDate()
                    .subtract(1, 'month')
                    .endOf('month');
            } else {
                let range =
                    m
                        .range(this.getStartDate(), this.getEndDate())
                        .diff('months') + 1;

                start = m(this.getStartDate()).subtract(range, 'month');

                end = m(this.getEndDate()).subtract(range, 'month');
            }

            this.selectRange(start, end);
            this.broadcastChange();
        },

        nextRange() {
            let start;
            let end;

            if (this.rangeIsExactlyOneYear()) {
                start = this.getStartDate()
                    .add(1, 'month')
                    .startOf('month');

                end = this.getEndDate()
                    .add(1, 'month')
                    .endOf('month');
            } else {
                let range =
                    m
                        .range(this.getStartDate(), this.getEndDate())
                        .diff('months') + 1;

                start = m(this.getStartDate()).add(range, 'month');

                end = m(this.getEndDate()).add(range, 'month');
            }

            this.selectRange(start, end);
            this.broadcastChange();
        },

        noDateDefine() {
            return this.startMonth == null && this.endMonth == null;
        },

        broadcastChange(s) {
            let payload;
            let start;
            let end;

            if (this.showDatePicker && this.startMonth) {
                start = this.startMonth;
                end = this.endMonth;

                payload = {
                    start: start.format(),
                    end: end.format(),
                };
            } else {
                start = null;
                end = null;
            }

            if (this.useVuex && this.showDatePicker) {
                this.$store.commit('setMonthReportRange', {
                    start,
                    end,
                });
            }

            this.$emit('onChange', payload);
        },

        fuelEnabled() {
            return Feature.hasFeature('fuel');
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
@import '../../sass/mixins';

$border-color: #ccc;

%body {
    display: inline-block;
    width: 28px;
    line-height: 28px;
    height: 28px;
    border-radius: 4px;
    text-align: center;
    @include user-select(none);
}

%body-hover {
    @extend %body;
    cursor: pointer;

    &:hover {
        background-color: #eeeeee;
    }
}

%top-divider {
    border-top: 1px solid $border-color;
    margin-top: 10px;
    padding-top: 10px;
}

.datepicker {
    position: relative;
}

.datepicker-inner {
    display: flex;
    width: 248px;

    > div {
        float: left;
    }
}

.datepicker-body {
    // width: 218px;
    padding: 10px 10px;
    // border-right: 1px solid $border-color;
    // display: inline-block;
}

.datepicker-shortcuts {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px 10px;
    width: 142px;
    vertical-align: top;
}

.datepicker-dateRange {
    span {
        display: inline-block;
        width: 54px;
        line-height: 54px;
        height: 54px;
        border-radius: 4px;
        text-align: center;
        margin: 2px 1.5px;
        font-size: 12px;
        font-weight: bold;
        cursor: pointer;
        text-transform: uppercase;
        user-select: none;

        &:hover {
            background-color: #eeeeee;
        }
    }
}

.datepicker-weekRange {
    span {
        @extend %body;
        font-size: 10px;
        font-weight: bold;
        text-transform: uppercase;
    }
}

.datepicker-day-disabled {
    color: #ddd !important;
    cursor: not-allowed !important;

    &:hover {
        background-color: transparent !important;
    }
}

.datepicker-day-different-month {
    color: #999;
}

.datepicker-current-month {
    font-weight: bold;
}

.datepicker-day-selected {
    background-color: #0cc2aa;
    border-radius: 0 !important;
    color: #fff;

    &:hover {
        background-color: #0cc2aa !important;
    }
}

.datepicker-day-start {
    border-bottom-left-radius: 4px !important;
    border-top-left-radius: 4px !important;
}

.datepicker-day-end {
    border-bottom-right-radius: 4px !important;
    border-top-right-radius: 4px !important;
}

.datepicker-ctrl {
    position: relative;
    height: 30px;
    line-height: 30px;
    font-weight: bold;
    text-align: center;

    p {
        @extend %body-hover;
        width: 65%;
    }

    span {
        @extend %body;
        position: absolute;
        cursor: pointer;
    }
}

.datepicker-popup {
    position: absolute;
    right: 0;
    border: 1px solid $border-color;
    border-radius: 5px;
    background: #fff;
    margin-top: 2px;
    z-index: 1000;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
}

.datepicker-timeInput {
    @extend %top-divider;

    div {
        margin-bottom: 0.5rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    p {
        font-size: 10px;
        font-weight: bold;
        margin-bottom: 0;
        text-transform: uppercase;
    }

    span.clickable {
        @extend %body-hover;
    }

    .colon {
        width: auto;
    }
}

.datepicker-minuteInput,
.datepicker-hourInput {
    margin-bottom: 1px;

    span {
        @extend %body-hover;
        height: 31px;
        width: 49px;
    }
}

.datepicker-preBtn,
.datepicker-nextBtn {
    &:hover {
        background-color: #eeeeee;
    }
}

.datepicker-preBtn {
    left: 2px;
}

.datepicker-nextBtn {
    right: 2px;
}

.shortcut-selector {
    @extend %top-divider;
    text-align: center;
}

.summary-group {
    width: 100%;
    display: flex;

    .chevron {
        display: inline-block;
        width: 0;
        height: 0;
        margin-right: 0.25rem;
        margin-left: 0.25rem;
        vertical-align: middle;
        content: '';
    }

    .prev {
        float: left;
        margin-right: -1px;

        &::after {
            @extend .chevron;
            border-top: 0.3em solid transparent;
            border-right: 0.3em solid;
            border-bottom: 0.3em solid transparent;
        }
    }

    .summary {
        overflow: hidden;
        flex-grow: 1;
        margin-left: -1px;
        margin-right: -1px;

        .btn {
            width: 100%;
            -webkit-border-radius: 0;
            -moz-border-radius: 0;
            border-radius: 0;
        }

        .popup-open {
            &::after {
                @extend .chevron;
                border-bottom: 0.3em solid;
                border-left: 0.3em solid transparent;
                border-right: 0.3em solid transparent;
            }
        }

        .popup-close {
            &::after {
                @extend .chevron;
                border-left: 0.3em solid transparent;
                border-right: 0.3em solid transparent;
                border-top: 0.3em solid;
            }
        }
    }

    .next {
        float: right;

        &::after {
            @extend .chevron;
            border-top: 0.3em solid transparent;
            border-left: 0.3em solid;
            border-bottom: 0.3em solid transparent;
        }
    }
}

.filter-mode {
    border-bottom: 1px solid #ccc;
    display: flex;
    padding: 10px;
    width: 100%;

    .btn {
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/Monthpicker.vue